import { Loading } from '@/components'
import { useI18nUrls } from '@/components/Functional/hooks/useI18nUrls'
import { useTracking } from '@/components/Functional/hooks/useTracking'
import { ActionDialog } from '@/components/Models/dialog/components/ActionDialog'
import type { PhotoFrameItem } from '@/schemas/v2/spot'
import { ActionIcon } from '@mantine/core'
import { IconCamera, IconHelp, IconPictureInPicture, IconRefresh, IconVideo, IconX } from '@tabler/icons-react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ConfirmationModal } from './ConfirmationModal'
import { FrameSettingsDialog } from './FrameSettingsDialog'
import { MessageModal } from './MessageModal'
import styles from './PhotoFrame.module.css'
import {
  getCameraStream,
  getMobileOS,
  getOptimalFrameRate,
  getSupportedMineTypeWithCodec,
  preloadImages,
  useInitCameraStream,
} from './helpers'

const calculateCanvasDimensions = (
  frame: { width: number; height: number },
  magnification: number,
  containerClientWidth: number,
  cw: number,
  ch: number,
) => {
  let canvasWidth = frame.width
  let canvasHeight = frame.height

  // 実際のサイズではなく、見た目の大きさ基準(CSS Display Size)でスケールを変換
  let scaleFactor = 1.0
  if (containerClientWidth > 0) {
    scaleFactor = cw / containerClientWidth
    scaleFactor *= 100 / magnification
    canvasWidth *= scaleFactor
    canvasHeight *= scaleFactor
  }

  // 画像がフォトフレームより大きい場合、アスペクト比を維持してリサイズ
  if (canvasWidth > cw || canvasHeight > ch) {
    const widthRatio = cw / canvasWidth
    const heightRatio = ch / canvasHeight
    const scale = Math.min(widthRatio, heightRatio)

    canvasWidth = Math.ceil(canvasWidth * scale)
    canvasHeight = Math.ceil(canvasHeight * scale)
  }

  return { canvasWidth, canvasHeight, scaleFactor }
}

type PhotoFrameProps = {
  photoFrame?: PhotoFrameItem[]
  onSaveSuccess?: () => void
  onClose?: () => void
  snsHashtag?: string
  /** 撮影後画面の拡散用SNS（context=ar_photo）取得に使うスポットID */
  spotId?: number
  /** AR起動時に自動再生する音声URL（#3261）。再生完了まで撮影・録画を無効化する。null/未指定なら即撮影可 */
  audioUrl?: string | null
}

const PhotoFrame = ({ photoFrame, onSaveSuccess, onClose, snsHashtag, spotId, audioUrl }: PhotoFrameProps) => {
  const canvasDimensions = { height: 1920, width: 1080 }
  const { t } = useTranslation()
  const { faqArCameraUrl } = useI18nUrls()

  const videoCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const offscreenCanvasRef = useRef<HTMLCanvasElement | null>(null)

  const mediaRecorderRef = useRef<MediaRecorder | undefined>(undefined)
  const chunks = useRef<Blob[]>([])
  const dataRef = useRef<string | undefined>(undefined)
  const recordingStartTime = useRef<number>(0)
  const recordingDuration = useRef<number>(0)

  const audioStreamRef = useRef<MediaStream | undefined>(undefined)

  // AR起動時に自動再生する音声（#3261）。再生完了まで撮影・録画をブロックする
  const guideAudioRef = useRef<HTMLAudioElement | null>(null)

  // パフォーマンス最適化用のref
  type CanvasRectCache = {
    rect: DOMRect
    videoCanvasRect: DOMRect
    ratio: { width: number; height: number }
    timestamp: number
  }
  const canvasRectsCache = useRef<Map<HTMLCanvasElement, CanvasRectCache>>(new Map())
  const isRecordingRef = useRef<boolean>(false)
  const previewDrawFrameId = useRef<number | undefined>(undefined)
  const orderedCanvasesRef = useRef<HTMLCanvasElement[]>([])
  const canvasListenerCleanups = useRef<Array<() => void>>([])

  const [mediaType, setMediaType] = useState<'video' | 'image'>('image')
  const [front, setFront] = useState<boolean>(false)
  const [record, setRecord] = useState(false)
  const [loading, setLoading] = useState(true)
  const [videoReady, setVideoReady] = useState(false)
  const [videoRecordingError, setVideoRecordingError] = useState<string>('')
  const [cameraPermissionDenied, setCameraPermissionDenied] = useState(false)
  // 音声（audioUrl）の再生が完了したか。音声が無い場合は最初から true（従来通り即撮影可）
  const [audioFinished, setAudioFinished] = useState(!audioUrl)
  const { tracking, getLocation } = useTracking({ page: 'photo_frame' })

  //ポップアップよう状態管理
  const [showConfirmationModal, setConfirmationModal] = useState(false)
  const [messageModal, setMessageModal] = useState({
    show: false,
    error: '',
  })
  const [showFrameSettings, setShowFrameSettings] = useState(false)
  const [frameVisibility, setFrameVisibility] = useState<boolean[]>(() => (photoFrame ?? []).map(() => true))

  const videoRef = useInitCameraStream(front, () => {
    setCameraPermissionDenied(true)
    setLoading(false)
  })
  const mobileOS = getMobileOS()

  // audioUrl があればマウント時に自動再生する（再生完了は <audio onEnded> で検知）。
  // 自動再生がブラウザにブロックされた場合は撮影を永久ブロックしないよう即解禁する。
  useEffect(() => {
    // audioUrl の有無に応じて再生完了フラグをリセットする。
    // audioUrl が初回 null → 後から入るケースでも、再生前に撮影/録画が有効化されないようにする。
    setAudioFinished(!audioUrl)
    const el = guideAudioRef.current
    if (!audioUrl || !el) return
    el.play().catch(() => setAudioFinished(true))
    return () => {
      el.pause()
    }
  }, [audioUrl])

  // 画像レイヤーを正しい順序で取得（movableな画像を先に、固定画像を後に）
  // これにより透かし画像（credit:true）が確実に最上位に表示される
  const getOrderedCanvases = useCallback(() => {
    const canvases = Array.from(document.getElementsByClassName(`${styles.image_canvas}`)) as HTMLCanvasElement[]
    const creditCanvases = canvases.filter((c) => c.dataset.credit === 'true')
    const normalCanvases = canvases.filter((c) => c.dataset.credit !== 'true')

    const fitCanvases = normalCanvases.filter((c) => !c.classList.contains(styles.movable))
    const movableCanvases = normalCanvases.filter((c) => c.classList.contains(styles.movable))

    // fit -> movable -> credit の順（fitが一番下、creditが一番上）
    return [...fitCanvases, ...movableCanvases, ...creditCanvases]
  }, [])

  const frameStyle = () => {
    let className = styles.frame
    if (mobileOS === 'iOS') {
      className += ` ${styles.frame_ios}`
    } else if (mobileOS === 'Android') {
      className += ' android'
    }
    return className
  }

  //前と後ろのカメラ変更する関数
  const toggleFront = () => {
    if (!videoReady) return

    setFront((s) => !s)
    setVideoReady(false)
  }

  //ウェッブカメラのstreamをキャンヴァスに表示
  const playPreview = useCallback(async () => {
    const frames = await preloadImages((photoFrame ?? []).map((s) => s.file))

    const [ch, cw] = [canvasDimensions.height, canvasDimensions.width]

    const videoCanvas = videoCanvasRef.current

    const offscreenCanvas = document.createElement('canvas')
    offscreenCanvas.height = ch
    offscreenCanvas.width = cw

    offscreenCanvasRef.current = offscreenCanvas

    const offScreenCtx = offscreenCanvas?.getContext('2d')
    const videoCtx = videoCanvas?.getContext('2d')

    const video = videoRef.current

    if (!(offScreenCtx && videoCtx && video && videoCanvas)) return

    video?.addEventListener('canplay', () => {
      setLoading(false)
      setVideoReady(true)
    })

    const canvasContainer = document.getElementById('canvas-container')
    if (!canvasContainer) return

    frames?.forEach((frame, index) => {
      const existingCanvas = canvasContainer.querySelector(`.${styles.image_canvas}-${index}`)
      if (existingCanvas) return

      const imageCanvas = document.createElement('canvas')
      const frameData = photoFrame?.[index]
      if (!frameData) {
        return
      }
      imageCanvas.className = `${styles.image_canvas} ${styles.image_canvas}-${index}`

      // 画像サイズをフォトフレームのサイズに合わせて調整
      // キャンバスの最大サイズはフォトフレームサイズ (1080x1920)
      imageCanvas.dataset.credit = frameData.credit ? 'true' : 'false'

      let drawWidth: number
      let drawHeight: number

      if (frameData.fit || frameData.credit) {
        // fit / credit フレームは内部解像度を常に cw×ch (1080×1920) に固定し、
        // CSS の width: 100% + 固有アスペクト比 (9:16) で表示サイズを自動計算させる。
        // これにより playPreview() の呼び出しタイミング・大画面・リサイズすべてに対応する。
        imageCanvas.width = cw
        imageCanvas.height = ch
        drawWidth = cw
        drawHeight = ch
        // style.width / style.height は設定しない（CSS 管理）
      } else {
        // movable フレームは JS でサイズを明示的に制御する
        const { canvasWidth, canvasHeight, scaleFactor } = calculateCanvasDimensions(
          frame,
          frameData.magnification,
          videoCanvas.getBoundingClientRect().width,
          cw,
          ch,
        )

        imageCanvas.width = canvasWidth
        imageCanvas.height = canvasHeight
        drawWidth = canvasWidth
        drawHeight = canvasHeight

        const styleWidth = `${canvasWidth / scaleFactor}px`
        const styleHeight = `${canvasHeight / scaleFactor}px`
        imageCanvas.style.width = styleWidth
        imageCanvas.style.height = styleHeight
        imageCanvas.dataset.initialStyleWidth = styleWidth
        imageCanvas.dataset.initialStyleHeight = styleHeight

        imageCanvas.className += ` ${styles.movable}`
        // 操作用イベントリスナーを追加する
        addImageManipulationListeners(imageCanvas)
      }

      const imageCtx = imageCanvas.getContext('2d')
      if (imageCtx) {
        // Android対応: 高品質な描画設定
        imageCtx.imageSmoothingEnabled = true
        imageCtx.imageSmoothingQuality = 'high'
        imageCtx.drawImage(frame, 0, 0, drawWidth, drawHeight)
      }

      // z-indexの設定：fit（固定）< movable（移動可能）< credit（透かし）
      let zIndex = 3 + index
      if (frameData.credit) {
        zIndex = 100 + index
      } else if (!frameData.fit) {
        // movableな画像はfitより上に表示
        zIndex = 50 + index
      }
      imageCanvas.style.zIndex = zIndex.toString()

      if (frameData.credit) {
        imageCanvas.style.visibility = 'hidden'
        imageCanvas.style.pointerEvents = 'none'
      }

      canvasContainer.appendChild(imageCanvas)
    })

    // キャンバス順序をキャッシュ（プレビュー描画の最適化用）
    orderedCanvasesRef.current = getOrderedCanvases()

    const drawFrame = () => {
      // 録画中はプレビュー描画を停止
      if (isRecordingRef.current) {
        return
      }

      videoCtx.drawImage(video, 0, 0, cw, ch)

      offScreenCtx.drawImage(videoCanvas, 0, 0)

      for (const canvas of orderedCanvasesRef.current) {
        if (canvas.style.visibility === 'hidden') continue
        offScreenCtx.drawImage(canvas, 0, 0)
      }

      previewDrawFrameId.current = requestAnimationFrame(drawFrame)
    }

    video.addEventListener(
      'loadedmetadata',
      () => {
        previewDrawFrameId.current = requestAnimationFrame(drawFrame)
      },
      { once: true },
    )
    video.load()
  }, [])

  useEffect(() => {
    playPreview()
  }, [])

  // canvas-container を video_canvas（カメラ撮影領域）に同期させる
  // ResizeObserver でリサイズ・初回マウント時に正確なサイズ・位置を反映する
  useEffect(() => {
    const videoCanvas = videoCanvasRef.current
    const canvasContainer = document.getElementById('canvas-container')
    if (!videoCanvas || !canvasContainer) return

    const syncSize = () => {
      const parent = canvasContainer.parentElement
      if (!parent) return
      const parentRect = parent.getBoundingClientRect()
      const videoRect = videoCanvas.getBoundingClientRect()

      // video_canvas がまだレイアウトされていない場合はスキップ
      if (videoRect.width === 0 || videoRect.height === 0) return

      // video_canvas（カメラ撮影領域）に合わせてサイズ・位置を同期することで
      // image_canvas の scale(1.01) がカメラ撮影領域に対して正確に101%になる
      canvasContainer.style.width = `${videoRect.width}px`
      canvasContainer.style.height = `${videoRect.height}px`
      canvasContainer.style.top = `${videoRect.top - parentRect.top}px`
      canvasContainer.style.left = `${videoRect.left - parentRect.left}px`
      canvasContainer.style.transform = 'none'
    }

    const observer = new ResizeObserver(syncSize)
    observer.observe(videoCanvas)
    syncSize()

    return () => {
      observer.disconnect()
    }
  }, [])

  useEffect(() => {
    if (videoRecordingError) {
      getLocation().then((v) => tracking({ page: 'photo_frame', optionalData: videoRecordingError, ...v }))
      setVideoRecordingError('')
    }
  }, [videoRecordingError])

  // 画像操作用のイベントリスナーを追加する関数
  const addImageManipulationListeners = (canvas: HTMLCanvasElement) => {
    let isDragging = false
    let isPinching = false

    canvas.dataset.lastX = '0'
    canvas.dataset.lastY = '0'
    canvas.dataset.translateX = '-50'
    canvas.dataset.translateY = '-50'

    canvas.dataset.initialPinchDistance = '0'
    canvas.dataset.scale = '1'
    canvas.dataset.initialScale = canvas.dataset.scale

    const handleMouseDown = (e: MouseEvent | TouchEvent) => {
      if (e instanceof TouchEvent && e.touches.length === 2) {
        e.preventDefault()
        isPinching = true
        canvas.dataset.initialPinchDistance = getPinchDistance(e).toString()
        canvas.dataset.initialScale = canvas.dataset.scale
        return
      }

      isDragging = true
      const clientX = e instanceof MouseEvent ? e.clientX : e.touches[0].clientX
      const clientY = e instanceof MouseEvent ? e.clientY : e.touches[0].clientY
      canvas.dataset.lastX = clientX.toString()
      canvas.dataset.lastY = clientY.toString()
    }

    const handleMouseMove = (e: MouseEvent | TouchEvent) => {
      if (isPinching && e instanceof TouchEvent && e.touches.length === 2) {
        e.preventDefault()
        const currentPinchDistance = getPinchDistance(e)
        canvas.dataset.scale = (
          Number(currentPinchDistance / Number(canvas.dataset.initialPinchDistance)) *
          Number(canvas.dataset.initialScale)
        ).toString()
        canvas.style.transform = `translate(${canvas.dataset.translateX}%, ${canvas.dataset.translateY}%) scale(${canvas.dataset.scale})`
        return
      }

      if (!isDragging) return

      const clientX = e instanceof MouseEvent ? e.clientX : e.touches[0].clientX
      const clientY = e instanceof MouseEvent ? e.clientY : e.touches[0].clientY

      const dx = clientX - Number(canvas.dataset.lastX)
      const dy = clientY - Number(canvas.dataset.lastY)

      const canvasWidth = canvas.clientWidth
      const canvasHeight = canvas.clientHeight

      canvas.dataset.translateX = (
        Number.parseFloat(canvas.dataset.translateX || '-50') +
        (dx / canvasWidth) * 100
      ).toString()
      canvas.dataset.translateY = (
        Number.parseFloat(canvas.dataset.translateY || '-50') +
        (dy / canvasHeight) * 100
      ).toString()

      canvas.style.transform = `translate(${canvas.dataset.translateX}%, ${canvas.dataset.translateY}%) scale(${canvas.dataset.scale})`

      canvas.dataset.lastX = clientX.toString()
      canvas.dataset.lastY = clientY.toString()

      if (e instanceof TouchEvent) {
        e.preventDefault()
      }
    }

    const handleMouseUp = () => {
      isDragging = false
      isPinching = false
    }

    const getPinchDistance = (e: TouchEvent): number => {
      if (e.touches.length < 2) return 0
      const [touch1, touch2] = [e.touches[0], e.touches[1]]
      return Math.sqrt((touch1.clientX - touch2.clientX) ** 2 + (touch1.clientY - touch2.clientY) ** 2)
    }

    canvas.addEventListener('mousedown', handleMouseDown)
    canvas.addEventListener('mousemove', handleMouseMove)
    canvas.addEventListener('mouseup', handleMouseUp)
    canvas.addEventListener('mouseleave', handleMouseUp)

    canvas.addEventListener('touchstart', handleMouseDown, { passive: false })
    canvas.addEventListener('touchmove', handleMouseMove, { passive: false })
    canvas.addEventListener('touchend', handleMouseUp)
    canvas.addEventListener('touchcancel', handleMouseUp)

    // クリーンアップ関数を返す
    const cleanup = () => {
      canvas.removeEventListener('mousedown', handleMouseDown)
      canvas.removeEventListener('mousemove', handleMouseMove)
      canvas.removeEventListener('mouseup', handleMouseUp)
      canvas.removeEventListener('mouseleave', handleMouseUp)
      canvas.removeEventListener('touchstart', handleMouseDown)
      canvas.removeEventListener('touchmove', handleMouseMove)
      canvas.removeEventListener('touchend', handleMouseUp)
      canvas.removeEventListener('touchcancel', handleMouseUp)
    }
    canvasListenerCleanups.current.push(cleanup)
  }

  const handleReset = () => {
    const canvases = document.getElementsByClassName(`${styles.movable}`)
    for (const canvas of Array.from(canvases)) {
      if (canvas instanceof HTMLCanvasElement) {
        canvas.style.transform = 'translate(-50%, -50%)'
        canvas.style.width = canvas.dataset.initialStyleWidth || ''
        canvas.style.height = canvas.dataset.initialStyleHeight || ''
        canvas.dataset.lastX = '0'
        canvas.dataset.lastY = '0'
        canvas.dataset.translateX = '-50'
        canvas.dataset.translateY = '-50'
        canvas.dataset.initialPinchDistance = '0'
        canvas.dataset.scale = '1'
        canvas.dataset.initialScale = canvas.dataset.scale
      }
    }
    // 全フレームの表示状態もリセット
    setFrameVisibility((photoFrame ?? []).map(() => true))
    const allCanvases = document.getElementsByClassName(`${styles.image_canvas}`)
    for (const canvas of Array.from(allCanvases)) {
      if (canvas instanceof HTMLCanvasElement) {
        const isCredit = canvas.dataset.credit === 'true'
        canvas.style.visibility = isCredit ? 'hidden' : 'visible'
        canvas.style.pointerEvents = isCredit ? 'none' : 'auto'
      }
    }
  }

  const handleToggleFrame = (index: number, visible: boolean) => {
    setFrameVisibility((prev) => {
      const next = [...prev]
      next[index] = visible
      return next
    })
    const canvasContainer = document.getElementById('canvas-container')
    if (!canvasContainer) return
    const canvas = canvasContainer.querySelector(`.${styles.image_canvas}-${index}`) as HTMLCanvasElement | null
    if (canvas) {
      canvas.style.visibility = visible ? 'visible' : 'hidden'
      canvas.style.pointerEvents = visible ? 'auto' : 'none'
    }
  }

  const getFrameLabel = (frameData: PhotoFrameItem, index: number): string => {
    if (frameData.fit) return t('spot_detail.ar.frame_bg', '背景画像')
    if (frameData.credit) return t('spot_detail.ar.frame_credit', 'クレジット')
    return t('spot_detail.ar.frame_object', 'オブジェクト')
  }

  useEffect(() => {
    const resumeVideoPlayback = () => {
      const video = videoRef.current

      if (!video) {
        return
      }

      // フォアグラウンドに戻ったら動画を再生する
      if (document.visibilityState === 'visible') {
        getCameraStream(front).then((stream) => {
          video.srcObject = stream ?? null
          if (stream) {
            video.play()
          }
        })
        handleScreenReturn()
      } else {
        const stream = video.srcObject as MediaStream
        if (stream) {
          for (const track of stream.getTracks()) {
            track.stop()
          }
        }

        const audioStream = audioStreamRef.current
        if (audioStream) {
          for (const track of audioStream.getTracks()) {
            track.stop()
          }
          audioStreamRef.current = undefined
        }

        if (video.srcObject) {
          video.pause()
          video.srcObject = null
        }
      }
    }

    // ページ離脱後にページに再アクセスされた際に、映像が停止しないように修正する.
    document.addEventListener('visibilitychange', resumeVideoPlayback)

    return () => {
      document.removeEventListener('visibilitychange', resumeVideoPlayback)
    }
  }, [front])

  const handleScreenReturn = () => {
    const mediaRecorder = mediaRecorderRef.current

    if (!mediaRecorder) return
    if (mediaRecorder.state !== 'recording') return

    // 録画フラグを停止
    isRecordingRef.current = false

    // プレビュー描画を再開
    // 録画中に停止していた描画ループを再起動する
    const drawFrame = () => {
      // 録画中はプレビュー描画を停止（念のため）
      if (isRecordingRef.current) {
        return
      }
      const videoCanvas = videoCanvasRef.current
      const video = videoRef.current
      const offscreenCanvas = offscreenCanvasRef.current

      if (!videoCanvas || !video || !offscreenCanvas) return

      const offScreenCtx = offscreenCanvas.getContext('2d')
      const videoCtx = videoCanvas.getContext('2d')
      const [ch, cw] = [canvasDimensions.height, canvasDimensions.width]

      if (!videoCtx || !offScreenCtx) return

      videoCtx.drawImage(video, 0, 0, cw, ch)
      offScreenCtx.drawImage(videoCanvas, 0, 0)

      // キャッシュされた順序付きキャンバスを使用
      const orderedCanvases = orderedCanvasesRef.current.length > 0 ? orderedCanvasesRef.current : getOrderedCanvases()
      for (const canvas of orderedCanvases) {
        offScreenCtx.drawImage(canvas, 0, 0)
      }

      previewDrawFrameId.current = requestAnimationFrame(drawFrame)
    }

    // 既存のループがあればキャンセル
    if (previewDrawFrameId.current) {
      cancelAnimationFrame(previewDrawFrameId.current)
    }
    previewDrawFrameId.current = requestAnimationFrame(drawFrame)

    //処理
    chunks.current = []
    mediaRecorder.stop()
    mediaRecorderRef.current = undefined
    setRecord(false)
  }

  // 保持している blob URL を解放する。
  // 撮り直しや再撮影のたびに createObjectURL するため、解放しないとページを離れるまでメモリに残る。
  const releaseDataRef = () => {
    const prev = dataRef.current
    if (prev?.startsWith('blob:')) {
      URL.revokeObjectURL(prev)
    }
    dataRef.current = undefined
  }

  //画像保存
  const handleImageCapture = () =>
    new Promise<void>((resolve) => {
      const videoCanvas = videoCanvasRef.current
      if (!videoCanvas) {
        resolve()
        return
      }

      const ctx = videoCanvas.getContext('2d')
      if (!ctx) {
        resolve()
        return
      }

      ctx.drawImage(videoCanvas, 0, 0)

      // 画像レイヤーを正しい順序で描画（movableな画像を先に、固定画像を後に）
      // これにより透かし画像（credit:true）が確実に最上位に表示される
      const orderedCanvases = getOrderedCanvases()

      for (const canvas of orderedCanvases) {
        // 非表示のフレームは撮影画像に含めない
        if (canvas.style.visibility === 'hidden') continue

        const rect = canvas.getBoundingClientRect()
        const videoCanvasRect = videoCanvas.getBoundingClientRect()
        const canvasRatio = {
          width: videoCanvas.width / videoCanvasRect.width,
          height: videoCanvas.height / videoCanvasRect.height,
        }

        const x = (rect.left - videoCanvasRect.left) * canvasRatio.width
        const y = (rect.top - videoCanvasRect.top) * canvasRatio.height
        const width = rect.width * canvasRatio.width
        const height = rect.height * canvasRatio.height

        ctx.drawImage(canvas, x, y, width, height)
      }

      // iOS/Safari は巨大な data: URL のダウンロードに失敗するため、動画と同じく blob URL に統一する。
      // プレビュー（<img src="blob:...">）を通すには CSP の img-src に blob: が必要（infrastructure 側で追加済み）。
      videoCanvas.toBlob(
        (blob) => {
          if (!blob) {
            resolve()
            return
          }
          releaseDataRef()
          dataRef.current = URL.createObjectURL(blob)
          resolve()
        },
        'image/jpeg',
        1.0,
      )
    })

  //動画録画開始
  const handleRecordStart = async () => {
    const mediaDevices = navigator.mediaDevices
    if (!mediaDevices) return

    const canvas = offscreenCanvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // 録画開始フラグを設定
    isRecordingRef.current = true
    setVideoRecordingError('')

    // プレビュー描画を停止
    if (previewDrawFrameId.current) {
      cancelAnimationFrame(previewDrawFrameId.current)
    }

    // キャッシュをクリア
    canvasRectsCache.current.clear()

    const drawFrame = () => {
      const videoCanvas = videoCanvasRef.current
      if (!videoCanvas) return
      const videoCtx = videoCanvas.getContext('2d')
      if (!videoCtx) return

      const video = videoRef.current
      if (!video) return

      const [ch, cw] = [canvasDimensions.height, canvasDimensions.width]
      videoCtx.drawImage(video, 0, 0, cw, ch)

      ctx.drawImage(videoCanvas, 0, 0)

      // 画像レイヤーを正しい順序で描画（movableな画像を先に、固定画像を後に）
      // これにより透かし画像（credit:true）が確実に最上位に表示される
      // キャッシュされた順序を使用（playPreviewと同様）
      const orderedCanvases = orderedCanvasesRef.current.length > 0 ? orderedCanvasesRef.current : getOrderedCanvases()

      // キャッシュされた座標情報を使用して描画
      for (const canvas of orderedCanvases) {
        // 非表示のフレームは録画映像に含めない
        if (canvas.style.visibility === 'hidden') continue

        let cachedData = canvasRectsCache.current.get(canvas)

        // キャッシュがないか、古い場合は更新
        if (!cachedData || Date.now() - cachedData.timestamp > 100) {
          const rect = canvas.getBoundingClientRect()
          const videoCanvasRect = videoCanvas.getBoundingClientRect()

          const canvasRatio = {
            width: videoCanvas.width / videoCanvasRect.width,
            height: videoCanvas.height / videoCanvasRect.height,
          }

          cachedData = {
            rect,
            videoCanvasRect,
            ratio: canvasRatio,
            timestamp: Date.now(),
          }

          canvasRectsCache.current.set(canvas, cachedData)
        }

        const x = (cachedData.rect.left - cachedData.videoCanvasRect.left) * cachedData.ratio.width
        const y = (cachedData.rect.top - cachedData.videoCanvasRect.top) * cachedData.ratio.height
        const width = cachedData.rect.width * cachedData.ratio.width
        const height = cachedData.rect.height * cachedData.ratio.height

        ctx.drawImage(canvas, x, y, width, height)
      }

      // 録画中のみ描画を継続
      if (isRecordingRef.current) {
        requestAnimationFrame(drawFrame)
      }
    }

    drawFrame()

    const frameRate = getOptimalFrameRate()
    const videoStream = frameRate ? canvas.captureStream(frameRate) : canvas.captureStream()
    audioStreamRef.current = audioStreamRef.current || (await mediaDevices.getUserMedia({ audio: true }))
    const audioStream = audioStreamRef.current
    const [audioTrack] = audioStream.getAudioTracks()
    if (audioTrack) {
      videoStream.addTrack(audioTrack)
    }

    const codec = getSupportedMineTypeWithCodec()

    if (!codec) {
      alert(t('spot_detail.ar.do_not_support', 'お客様のブラウザーではこの機能はサポートされていません'))
      setVideoRecordingError('AR Photo Not supported.')
      return
    }

    const mediaRecorderOptions: MediaRecorderOptions = {
      mimeType: codec,
    }

    // MediaRecorderを毎回新しく作成
    mediaRecorderRef.current = new MediaRecorder(videoStream, mediaRecorderOptions)
    console.log(`selected: mimeType ${mediaRecorderRef.current.mimeType}`)

    const mediaRecorder = mediaRecorderRef.current
    chunks.current = []

    mediaRecorder.ondataavailable = (e) => {
      chunks.current.push(e.data)
    }

    mediaRecorder.onerror = (event) => {
      console.error('MediaRecorder error:', event)
      isRecordingRef.current = false
      setRecord(false)
      setVideoRecordingError('MediaRecorder Error occurred.')
    }

    recordingStartTime.current = Date.now()
    mediaRecorder.start()
  }

  //動画録画を止める
  const handleRecordStop = () =>
    new Promise((resolve) => {
      if (!mediaRecorderRef.current || mediaRecorderRef.current.state !== 'recording') {
        resolve('')
        return
      }

      // 録画フラグを停止
      isRecordingRef.current = false

      // プレビュー描画を再開
      // 録画中に停止していた描画ループを再起動する
      const drawFrame = () => {
        // 録画中はプレビュー描画を停止（念のため）
        if (isRecordingRef.current) {
          return
        }
        const videoCanvas = videoCanvasRef.current
        const video = videoRef.current
        const offscreenCanvas = offscreenCanvasRef.current

        if (!videoCanvas || !video || !offscreenCanvas) return

        const offScreenCtx = offscreenCanvas.getContext('2d')
        const videoCtx = videoCanvas.getContext('2d')
        const [ch, cw] = [canvasDimensions.height, canvasDimensions.width]

        if (!videoCtx || !offScreenCtx) return

        videoCtx.drawImage(video, 0, 0, cw, ch)
        offScreenCtx.drawImage(videoCanvas, 0, 0)

        // キャッシュされた順序付きキャンバスを使用
        const orderedCanvases =
          orderedCanvasesRef.current.length > 0 ? orderedCanvasesRef.current : getOrderedCanvases()
        for (const canvas of orderedCanvases) {
          offScreenCtx.drawImage(canvas, 0, 0)
        }

        previewDrawFrameId.current = requestAnimationFrame(drawFrame)
      }

      // 既存のループがあればキャンセル
      if (previewDrawFrameId.current) {
        cancelAnimationFrame(previewDrawFrameId.current)
      }
      previewDrawFrameId.current = requestAnimationFrame(drawFrame)

      // 録画時間を計算
      recordingDuration.current = Date.now() - recordingStartTime.current
      console.log('Recording duration:', recordingDuration.current, 'ms')

      mediaRecorderRef.current.onstop = (e) => {
        const mimeType = getSupportedMineTypeWithCodec()

        console.log('Creating blob with MIME type:', mimeType)
        console.log('Chunks count:', chunks.current.length)

        const blob = new Blob(chunks.current, { type: mimeType })

        // 以前は iOS のみ FileReader で data: URL に変換していたが、
        // base64 化で 1.33 倍に膨らんだ結果 Safari のダウンロードが失敗していたため blob URL に統一する。
        // 再生・保存とも CSP の media-src に blob: があるので通る。
        releaseDataRef()
        const objectUrl = URL.createObjectURL(blob)
        dataRef.current = objectUrl

        console.log('Created object URL:', objectUrl)
        console.log('Blob size:', blob.size)
        console.log('Blob type:', blob.type)

        resolve(objectUrl)
      }

      mediaRecorderRef.current.stop()
    })

  //シャッターをおす関数
  const onShutterClick = async () => {
    // 音声（#3261）の再生が完了するまでは撮影・録画を開始させない
    if (!audioFinished) return
    if (mediaType === 'image') {
      // toBlob は非同期なので、blob URL が確定してから確認モーダルを開く
      await handleImageCapture()
      openModal()
    } else if (mediaType === 'video') {
      if (!record) {
        setRecord(true)
        await handleRecordStart()
      } else {
        setRecord(false)

        await handleRecordStop().then(() => {
          openModal()
        })
      }
    }
  }

  //確認ポップアップを開く
  const openModal = () => {
    setConfirmationModal(true)
  }
  //取り直し
  const onModalClose = () => {
    releaseDataRef()
    setConfirmationModal(false)
  }
  //メディア保存
  const onSave = () => {
    saveMedia()
  }

  const saveMedia = () => {
    try {
      const url = dataRef.current

      if (!url) {
        console.error('data ref is empty')
        return
      }

      const link = document.createElement('a')
      const codec = getSupportedMineTypeWithCodec()
      const suffix = codec.includes('webm') ? '.webm' : '.mp4'

      const date = Date.now()
      link.download = `${date}${mediaType === 'video' ? suffix : '.jpg'}`
      link.href = url
      // iOS/Safari(WebKit) は document に接続されていない <a> の click() では
      // ダウンロードが実行されない（確認ダイアログだけ出て保存されない）ため、
      // 一度 DOM に追加してからクリックし、直後に取り除く。
      // 復元コードのダウンロード（RestoreShowTab）と同じ手順に揃えている。
      link.style.display = 'none'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      setConfirmationModal(false)
      toggleMessageModal()
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : t('common.error', 'エラーが発生しました'))
      toggleMessageModal()
    }
  }

  //保存成功の時開くメッセージ
  const onProceed = () => {
    toggleMessageModal(true)
    onSaveSuccess?.()
  }

  const toggleMessageModal = (hide = false) => {
    setMessageModal((s) => ({ ...s, show: !hide }))
  }
  const setErrorMessage = (text: string) => {
    setMessageModal((s) => ({ ...s, error: text }))
  }
  const handleARClose = () => {
    onClose?.()
  }

  useEffect(() => {
    const cbk = () => {
      const frame = document.getElementById('ar_frame')
      if (!frame) return
      const { height: frameHeight } = frame.getBoundingClientRect()
      frame.style.setProperty('--frame-height', `${frameHeight.toFixed(2)}px`)
    }
    cbk()
    window.addEventListener('resize', cbk)

    return () => {
      window.removeEventListener('resize', cbk)
    }
  }, [])

  useEffect(() => {
    return () => {
      const audio = audioStreamRef.current

      if (audio) {
        for (const t of audio.getTracks()) {
          t.stop()
        }
      }

      // プレビュー描画を停止
      if (previewDrawFrameId.current) {
        cancelAnimationFrame(previewDrawFrameId.current)
      }

      // canvasイベントリスナーのクリーンアップ
      for (const cleanup of canvasListenerCleanups.current) {
        cleanup()
      }
      canvasListenerCleanups.current = []

      // 録画フラグをリセット
      isRecordingRef.current = false
    }
  }, [])

  return (
    <div className={`${styles['frame-container']}`}>
      {loading && <Loading />}

      {/* AR起動時に自動再生する音声（#3261）。再生完了まで撮影・録画を無効化する */}
      {audioUrl && (
        // biome-ignore lint/a11y/useMediaCaption: ARフォトのガイド音声にキャプショントラックは供給されない
        <audio ref={guideAudioRef} src={audioUrl} onEnded={() => setAudioFinished(true)} className="hidden" />
      )}

      <div className={frameStyle()} id="ar_frame">
        <canvas
          className={styles.video_canvas}
          ref={videoCanvasRef}
          height={canvasDimensions.height}
          width={canvasDimensions.width}
        />
        <div id="canvas-container" className={styles['canvas-container']} />
        <video className={styles.video} src="" autoPlay ref={videoRef} muted playsInline />

        {/* フローティング✕ボタン（右上） */}
        <div className="absolute right-4 top-4 z-[100]">
          <ActionIcon
            variant="subtle"
            color="white"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.4)' }}
            onClick={handleARClose}
            aria-label={t('common.close', '閉じる')}
          >
            <IconX size={24} stroke={1.5} aria-hidden="true" />
          </ActionIcon>
        </div>
      </div>

      {/* 底部コントロール（flex-shrink-0 でノーマルフロー配置） */}
      <div className="flex-shrink-0 bg-black">
        {/* 3カラムレイアウト: [使い方・リセット] | [撮影+モード切替] | [カメラ切替] */}
        <div className="grid grid-cols-3 items-center px-4 pt-3 pb-6">
          {/* 左: 使い方 + リセット */}
          <div className="flex items-center justify-center gap-3">
            <a
              href={faqArCameraUrl}
              target="_blank"
              rel="noreferrer noopener"
              className="flex flex-col items-center gap-1"
            >
              <div className="flex items-center justify-center size-[54px] rounded-[8px] bg-[rgba(255,255,255,0.2)]">
                <IconHelp size={32} className="text-white" />
              </div>
              <span className="text-white text-[12px]">{t('spot_detail.ar.usage', '使い方')}</span>
            </a>
            <button
              type="button"
              onClick={() => setShowFrameSettings(true)}
              className="flex flex-col items-center gap-1"
            >
              <div className="flex items-center justify-center size-[54px] rounded-[8px] bg-[rgba(255,255,255,0.2)]">
                <IconPictureInPicture size={32} className="text-white" />
              </div>
              <span className="text-white text-[12px]">{t('spot_detail.ar.frame', 'フレーム')}</span>
            </button>
          </div>

          {/* 中央: 撮影ボタン + モード切替 (縦積み) */}
          <div className="flex flex-col items-center gap-2">
            <button
              type="button"
              onClick={onShutterClick}
              disabled={!audioFinished}
              className={`relative size-[64px] ${audioFinished ? '' : 'opacity-40'}`}
              aria-label={t('spot_detail.ar.shutter', '撮影')}
            >
              <div className="absolute inset-0 rounded-full border-[3px] border-white" />
              {mediaType === 'video' && record ? (
                <div className="absolute inset-[14px] rounded-[8px] bg-[#FF0000]" />
              ) : (
                <div
                  className={`absolute inset-[4px] rounded-full ${mediaType === 'video' ? 'bg-[#FF0000]' : 'bg-white'}`}
                />
              )}
            </button>
            <div className="flex bg-[#424242] rounded-full">
              <button
                type="button"
                onClick={() => setMediaType('image')}
                className={`px-3 py-1 rounded-full transition-colors ${mediaType === 'image' ? 'bg-main' : ''}`}
                aria-label={t('spot_detail.ar.photo_mode', '写真モード')}
              >
                <IconCamera size={20} className={mediaType === 'image' ? 'text-sub' : 'text-white'} />
              </button>
              <button
                type="button"
                onClick={() => setMediaType('video')}
                className={`px-3 py-1 rounded-full transition-colors ${mediaType === 'video' ? 'bg-main' : ''}`}
                aria-label={t('spot_detail.ar.video_mode', 'ビデオモード')}
              >
                <IconVideo size={20} className={mediaType === 'video' ? 'text-sub' : 'text-white'} />
              </button>
            </div>
          </div>

          {/* 右: カメラ切替 */}
          <div className="flex items-center justify-center">
            <button
              type="button"
              onClick={toggleFront}
              className="flex flex-col items-center gap-1"
              disabled={!videoReady}
            >
              <div className="flex items-center justify-center size-[54px] rounded-[8px] bg-[rgba(255,255,255,0.2)]">
                <IconRefresh size={32} className="text-white" />
              </div>
              <span className="text-white text-[12px]">{t('spot_detail.ar.swap_camera', 'カメラ切替')}</span>
            </button>
          </div>
        </div>
      </div>

      <ConfirmationModal
        show={showConfirmationModal}
        dataSrc={dataRef.current}
        mediaType={mediaType}
        onClose={onModalClose}
        onSave={onSave}
        recordingDuration={recordingDuration.current}
      />

      <MessageModal
        show={messageModal.show}
        error={messageModal.error}
        onProceed={onProceed}
        hashtag={snsHashtag}
        spotId={spotId}
      />

      <FrameSettingsDialog
        open={showFrameSettings}
        onClose={() => setShowFrameSettings(false)}
        frames={(photoFrame ?? []).map((f, i) => ({
          index: i,
          label: getFrameLabel(f, i),
          thumbnailUrl: f.file,
          visible: frameVisibility[i] ?? true,
        }))}
        onToggleFrame={handleToggleFrame}
        onReset={handleReset}
      />

      {/* カメラ許可エラーダイアログ（NotAllowedError 時） */}
      <ActionDialog
        selectorState={cameraPermissionDenied}
        variant="message"
        zIndex={300}
        title={t('scanner.camera_permission_denied.title', 'カメラの許可が必要です')}
        description={t(
          'scanner.camera_permission_denied.description_ar',
          'ARカメラを使用するにはカメラの許可が必要です。<br>ブラウザの設定からカメラの許可をしてください。',
        )}
        cancelLabel={t('common.close', '閉じる')}
        closeAction={() => {
          setCameraPermissionDenied(false)
          onClose?.()
        }}
      />
    </div>
  )
}

export { PhotoFrame }
export default PhotoFrame
